import { useCallback, useEffect, JSX } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useLocalStorage } from 'react-use'
import { z } from 'zod'

import { SplitsLink as Link } from '~/components/Link'
import {
  Message,
  RadioGroupInput,
  TextInput,
} from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { useActiveOrg, useActiveOrgUpdates } from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'
import { OrgInvitation, OrgMemberRole } from '~/types'
import { sendTrackingData } from '~/utils/tracking'

const InviteMemberSchema = z.object({
  email: z.string().trim().email('Please enter a valid email address'),
  role: z.enum([OrgMemberRole.Member, OrgMemberRole.Owner]),
})

export const InviteMemberDialogContent = ({
  closeDialog,
  onAddInvitation,
}: {
  closeDialog: () => void
  onAddInvitation?: (invitation: OrgInvitation) => void
}): JSX.Element => {
  const { serverPostRequest, isProcessing } = useServerRequests()
  const { org } = useActiveOrg()

  const [defaultRoleSelectionLocal, setDefaultRoleSelectionLocal] =
    useLocalStorage<OrgMemberRole>(
      `splits.${org?.id}.defaultRoleSelection`,
      OrgMemberRole.Owner,
    )

  const { addInvitation } = useActiveOrgUpdates()

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    resolver: zodResolver(InviteMemberSchema),
    mode: 'onSubmit',
    defaultValues: {
      email: '',
      role: defaultRoleSelectionLocal ?? OrgMemberRole.Owner,
    },
  })

  const email = useWatch({ control, name: 'email' })
  const role = useWatch({ control, name: 'role' })

  // Watch the role field and sync with localStorage
  const watchedRole = useWatch({
    control,
    name: 'role',
  })

  useEffect(() => {
    if (watchedRole !== undefined) {
      setDefaultRoleSelectionLocal(watchedRole)
    }
  }, [watchedRole, setDefaultRoleSelectionLocal])

  const onSubmit: SubmitHandler<z.infer<typeof InviteMemberSchema>> =
    useCallback(
      async (data) => {
        sendTrackingData('submitInviteOrgMemberModal')

        const result = await serverPostRequest<OrgInvitation>({
          path: '/api/org/user/invite',
          params: {
            orgId: org!.id,
            email: data.email,
            role: data.role,
          },
          successMessage: 'Invite sent',
          errorMessage: 'Failed to invite user',
        })
        if (result) {
          if (result.data) {
            addInvitation(result.data)
          }
          onAddInvitation?.(result.data)
          closeDialog()
        }
      },
      [serverPostRequest, org, closeDialog, addInvitation, onAddInvitation],
    )

  return (
    <SplitsDialogContent
      id="addOrgMember"
      title="Invite workspace member"
      variant="cta"
      isLoading={isProcessing}
      actionButton={{
        text: 'Send invite',
        onClick: handleSubmit(onSubmit),
        eventProperties: { email, role },
        isDisabled: !isValid,
      }}
    >
      <p>New members will need to accept your invite</p>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <TextInput
          control={control}
          inputName="email"
          placeholder="name@example.com"
          autoFocus
          autoComplete="email"
          label="Email"
        />
        <RadioGroupInput
          label="Role"
          control={control}
          inputName={'role'}
          options={[
            {
              value: OrgMemberRole.Owner,
              label: 'Owner',
              description: 'Admin level access to the entire workspace',
            },
            {
              value: OrgMemberRole.Member,
              label: 'Member',
              description: 'Create and sign transactions',
            },
          ]}
        />
      </form>
      <Message variant="info">
        <div>
          You can add them as signers on accounts once they accept and add a
          passkey. Visit the{' '}
          <Link
            path="https://splits.org/docs/accounts/signers"
            underline
            target="tab"
          >
            docs
          </Link>{' '}
          for more information.
        </div>
      </Message>
    </SplitsDialogContent>
  )
}
